import React, { useContext, useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  Spinner,
  Button,
  Modal,
} from "flowbite-react";
import { Store } from "../context/store";
import apiRequest from "../utils/apiRequest";
import {
  HiDocumentText,
  HiDownload,
  HiX,
  HiCurrencyDollar,
  HiCalendar,
  HiTrendingUp,
} from "react-icons/hi";

// Import the necessary hook
import { usePDF } from "react-to-pdf";
import DashboardLayout from "../components/layout/DashboardLayout";

const toTitle = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// --- Payslip UI Template for PDF Generation ---
// This is the component that will be converted to PDF. It must use inline styles or non-tailwind CSS for reliable printing.
// I will use Tailwind classes but keep the design simple and fixed.

const PayslipPDFView = React.forwardRef(({ payslip, employeeDetails }, ref) => {
  // Helper function for currency formatting
  const formatCurrency = (amount) => {
    if (typeof amount !== "number") return "₦0.00";
    return `₦${amount.toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
  };

  if (!payslip || !employeeDetails) return null;

  // Standardized table style for better PDF output
  const tableClass = "w-full border-collapse text-left text-sm";
  const thClass = "border p-2 bg-gray-100 font-bold text-gray-700";
  const tdClass = "border p-2 text-gray-800";
  const totalThClass =
    "border p-2 bg-indigo-100 font-extrabold text-indigo-700";
  const totalTdClass = "border p-2 font-extrabold text-indigo-800";

  return (
    <div
      ref={ref}
      className="p-8 bg-white text-gray-800 max-w-4xl mx-auto shadow-2xl"
      style={{ minHeight: "297mm" }}
    >
      <div className="flex justify-between items-start border-b-4 border-indigo-600 pb-4 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold text-indigo-800">PAYSLIP</h1>
          <p className="text-sm text-gray-500 mt-1">
            **{payslip.payPeriod.month} {payslip.payPeriod.year}**
          </p>
        </div>
        <div className="text-right">
          <h2 className="text-xl font-bold">RoyalMines Property Limited</h2>
          <p className="text-sm">
            Plot 251/252 Herbert Macaulay way Millennium Plaza,
          </p>
          <p className="text-sm">
            Central Business District Opposite NNPC Towers.
          </p>
          <p className="text-sm">Abuja, Nigeria.</p>
          <p className="text-sm">info@royalmines.com</p>
        </div>
      </div>

      {/* Employee Details Section */}
      <div className="mb-8 p-4 bg-gray-50 border rounded-lg">
        <h3 className="text-lg font-bold mb-3 text-indigo-600">
          Employee Information
        </h3>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div>
            <span className="font-semibold">Name:</span>
            {employeeDetails.firstName} {employeeDetails.lastName}
          </div>
          <div>
            <span className="font-semibold">Role:</span>
            {employeeDetails.role.title}
          </div>
          <div>
            <span className="font-semibold">Employee ID:</span>
            {employeeDetails.id?.substring(0, 5).toUpperCase()}
          </div>
          <div>
            <span className="font-semibold">Pay Date:</span>
            {new Date(payslip.issuedDate).toLocaleDateString()}
          </div>
        </div>
      </div>

      {/* Earnings and Deductions Table */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Earnings */}
        <div>
          <h3 className="text-lg font-bold mb-3 text-green-600">Earnings</h3>
          <table className={tableClass}>
            <thead>
              <tr>
                <th className={thClass}>Description</th>
                <th className={`${thClass} text-right`}>Amount (₦)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={tdClass}>Basic Salary</td>
                <td className={`${tdClass} text-right`}>
                  {formatCurrency(payslip.basicSalary)}
                </td>
              </tr>
              <tr>
                <td className={tdClass}>Housing Allowance</td>
                <td className={`${tdClass} text-right`}>
                  {formatCurrency(payslip.housingAllowance)}
                </td>
              </tr>
              <tr>
                <td className={tdClass}>Transport Allowance</td>
                <td className={`${tdClass} text-right`}>
                  {formatCurrency(payslip.transportAllowance)}
                </td>
              </tr>
              <tr>
                <td className={tdClass}>Education Allowance</td>
                <td className={`${tdClass} text-right`}>
                  {formatCurrency(payslip.educationAllowance)}
                </td>
              </tr>
              <tr>
                <td className={tdClass}>Training Allowance</td>
                <td className={`${tdClass} text-right`}>
                  {formatCurrency(payslip.trainingAllowance)}
                </td>
              </tr>

              <tr>
                <th className={totalThClass}>GROSS SALARY</th>
                <th className={`${totalTdClass} text-right`}>
                  {formatCurrency(payslip.grossSalary)}
                </th>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Deductions */}
        <div>
          <h3 className="text-lg font-bold mb-3 text-red-600">Deductions</h3>
          <table className={tableClass}>
            <thead>
              <tr>
                <th className={thClass}>Deductions</th>
                <th className={`${thClass} text-right`}>Amount (₦)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={tdClass}>PAYEE</td>
                <td className={`${tdClass} text-right`}>
                  {formatCurrency(payslip.payee)}
                </td>
              </tr>
              <tr>
                <td className={tdClass}>Pension Contribution</td>
                <td className={`${tdClass} text-right`}>
                  {formatCurrency(payslip.pensionContribution)}
                </td>
              </tr>
              <tr>
                <td className={tdClass}>National Housing Fund (NHF)</td>
                <td className={`${tdClass} text-right`}>
                  {formatCurrency(payslip.nhf)}
                </td>
              </tr>
              <tr>
                <td className={tdClass}>Other Deductions</td>
                <td className={`${tdClass} text-right`}>
                  {formatCurrency(payslip.otherDeductions)}
                </td>
              </tr>

              <tr>
                <th className={totalThClass}>TOTAL DEDUCTIONS</th>
                <th className={`${totalTdClass} text-right`}>
                  {formatCurrency(payslip.totalDeductions)}
                </th>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Net Pay Summary */}
      <div className="flex justify-end mt-10">
        <div className="w-full lg:w-1/2 p-4 bg-indigo-50 border-2 border-indigo-600 rounded-lg text-center shadow-lg">
          <p className="text-xl font-bold text-gray-700 mb-2">Net Income</p>
          <p className="text-4xl font-extrabold text-indigo-800">
            {formatCurrency(payslip.netIncome || payslip.netPay) }
          </p>
        </div>
      </div>

      <p className="mt-12 text-center text-xs text-gray-500">
        This is an electronically generated document and does not require a
        signature.
      </p>
    </div>
  );
});

// --- Main Payslip Component ---

function Payslip() {
  let store = useContext(Store);
  let [baseUrl] = store.url;
  // Assuming employee details are available in store or can be fetched
  let [currentUser] = store.loggedInUser;

  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // New state for modal and selected payslip
  const [selectedPayslip, setSelectedPayslip] = useState(null);

  // usePDF hook setup
  const { toPDF, targetRef } = usePDF({
    filename: `${toTitle(currentUser.firstName)}_${toTitle(
      currentUser.lastName
    )}_${selectedPayslip?.payPeriod.month}_${selectedPayslip?.payPeriod.year}`,
  });

  const fetchPayslips = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("royal-token");
      const url = `${baseUrl}/payslips/my`;
      const data = await apiRequest(url, "GET", null, token);
      setPayslips(data.data);
    } catch (err) {
      setError(err.message || "Failed to fetch payslips.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => {
    fetchPayslips();
  }, [fetchPayslips]);

  // Open the modal with the selected payslip details
  const openPayslipModal = (payslip) => {
    setSelectedPayslip(payslip);
  };

  // Close the modal
  const closePayslipModal = () => {
    setSelectedPayslip(null);
  };

  // Function to handle the PDF download using react-to-pdf
  const handlePDFDownload = () => {
    if (selectedPayslip) {
      toPDF();
    }
  };

  // Function to handle external PDF download (kept for compatibility)
  const handleExternalDownload = (pdfUrl) => {
    window.open(pdfUrl, "_blank");
  };

  const formatCurrency = (amount) =>
    Number(amount || 0).toLocaleString("en-NG", {
      style: "currency",
      currency: "NGN",
      maximumFractionDigits: 0,
    });

  const analytics = useMemo(() => {
    const totalPayslips = payslips.length;
    const latestPayslip = payslips[0];
    const totalNetPay = payslips.reduce(
      (sum, payslip) => sum + Number(payslip.netIncome || payslip.netPay || 0),
      0,
    );
    const totalGrossPay = payslips.reduce(
      (sum, payslip) => sum + Number(payslip.grossSalary || 0),
      0,
    );

    return {
      totalPayslips,
      latestPeriod: latestPayslip
        ? `${latestPayslip.payPeriod.month} ${latestPayslip.payPeriod.year}`
        : "N/A",
      totalNetPay,
      totalGrossPay,
    };
  }, [payslips]);

  const AnalyticsCard = ({
    title,
    value,
    colorClass,
    icon: Icon,
    currency = false,
  }) => (
    <Card className={`shadow-lg p-2 dark:bg-gray-800 border-l-4 ${colorClass}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase">
            {title}
          </p>
          <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white mt-1">
            {currency ? formatCurrency(value) : value}
          </h2>
        </div>
        <Icon className="w-8 h-8 opacity-60" />
      </div>
    </Card>
  );

  return (
    <>
      <title>My Payslips</title>
      <DashboardLayout active="payslip" contentClassName="lg:p-8">
        <h5 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-6 flex items-center">
          <HiCurrencyDollar className="w-8 h-8 mr-3 text-indigo-600" />
          Payslip Management
        </h5>

        <hr className="my-6 border-gray-200 dark:border-gray-700" />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <AnalyticsCard
            title="Total Payslips"
            value={analytics.totalPayslips}
            colorClass="text-indigo-500 border-indigo-500"
            icon={HiDocumentText}
          />
          <AnalyticsCard
            title="Latest Period"
            value={analytics.latestPeriod}
            colorClass="text-cyan-500 border-cyan-500"
            icon={HiCalendar}
          />
          <AnalyticsCard
            title="Total Net Pay"
            value={analytics.totalNetPay}
            colorClass="text-green-500 border-green-500"
            icon={HiCurrencyDollar}
            currency
          />
          <AnalyticsCard
            title="Total Gross Pay"
            value={analytics.totalGrossPay}
            colorClass="text-amber-500 border-amber-500"
            icon={HiTrendingUp}
            currency
          />
        </div>

            {loading ? (
              <div className="flex justify-center items-center h-64">
                <Spinner size="xl" color="info" />
              </div>
            ) : error ? (
              <Card className="shadow-lg">
                <p className="text-red-500 dark:text-red-400 text-center">
                  {error}
                </p>
              </Card>
            ) : payslips.length === 0 ? (
              <Card className="shadow-lg">
                <p className="text-gray-500 dark:text-gray-400 text-center">
                  No payslips available yet.
                </p>
              </Card>
            ) : (
              // Payslip List Grid
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {payslips?.map((payslip) => (
                  <Card
                    key={payslip.id}
                    className="shadow-lg hover:shadow-xl transition-shadow dark:bg-gray-800 border-t-4 border-blue-500"
                  >
                    <div className="flex items-center space-x-3 mb-2">
                      <HiDocumentText className="w-8 h-8 text-blue-600 dark:text-blue-400 shrink-0" />
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                        {`${payslip.payPeriod.month} / ${payslip.payPeriod.year}`}
                      </h3>
                    </div>
                    <div className="space-y-2 mb-4">
                      <p className="text-sm">
                        <span className="font-semibold text-gray-600 dark:text-gray-300">
                          Net Income:
                        </span>{" "}
                        <span className="text-xl font-extrabold text-green-600 dark:text-green-400 block mt-1">
                          ₦
                          {payslip.netIncome?.toLocaleString() ||
                            payslip.netPay?.toLocaleString()}{" "}
                        </span>
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Issued:{" "}
                        {new Date(payslip.issuedDate).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => openPayslipModal(payslip)}
                        color="indigo"
                        size="sm"
                        className="w-full"
                      >
                        <HiDocumentText className="mr-2 h-4 w-4" />
                        View Payslip
                      </Button>

                      {/* Optional: Keep the external download button if pdfUrl is available */}
                      {payslip.pdfUrl && (
                        <Button
                          onClick={() => handleExternalDownload(payslip.pdfUrl)}
                          color="gray"
                          size="sm"
                        >
                          <HiDownload className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
      </DashboardLayout>

      {/* --- Payslip View Modal --- */}
      <Modal
        show={!!selectedPayslip}
        size="7xl"
        onClose={closePayslipModal}
        popup
        theme={{
          root: {
            base: "fixed inset-0 z-[2000] h-full w-full overflow-y-auto overflow-x-hidden md:inset-0 md:h-full",
          },
          content: {
            inner:
              "relative rounded-lg bg-white shadow dark:bg-gray-700 flex flex-col max-h-full",
          },
          // The backdrop needs a high z-index too
          backdrop: "fixed inset-0 z-[1990] bg-gray-900/50 dark:bg-gray-900/80",
        }}
      >
        <Modal.Header>
          <div className="text-xl font-bold p-2">
            Payslip for {selectedPayslip?.payPeriod.month} /{" "}
            {selectedPayslip?.payPeriod.year}
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="h-[75vh] overflow-y-auto p-4 bg-gray-100 dark:bg-gray-900 border rounded-lg">
            {/* The Payslip PDF Template is rendered here */}
            <PayslipPDFView
              ref={targetRef}
              payslip={selectedPayslip}
              employeeDetails={currentUser}
            />
          </div>
        </Modal.Body>
        <Modal.Footer className="flex justify-between">
          <Button
            onClick={handlePDFDownload}
            color="success"
            className="font-bold"
          >
            <HiDownload className="mr-2 h-5 w-5" />
            Download Payslip (PDF)
          </Button>
          <Button onClick={closePayslipModal} color="light">
            <HiX className="mr-2 h-5 w-5" />
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      {/* --- End Payslip View Modal --- */}
    </>
  );
}

export default Payslip;
